import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Plus, Phone, Mail, FileText, DollarSign, CheckCircle, XCircle, TrendingUp, AlertCircle, GripVertical, Building2, User } from 'lucide-react';

const SalesPipeline = () => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDeal, setShowAddDeal] = useState(false);
  const [draggedDeal, setDraggedDeal] = useState(null);

  // Pipeline stages - simple and clear
  const stages = [
    { 
      id: 'new_lead', 
      name: '📞 New Lead', 
      color: 'bg-blue-100 border-blue-300',
      description: 'Someone reached out or you found them',
      nextStep: 'Give them a call or send an email'
    },
    { 
      id: 'contacted', 
      name: '💬 Contacted', 
      color: 'bg-purple-100 border-purple-300',
      description: 'You\'ve talked to them about the job',
      nextStep: 'Schedule a site visit or send a quote'
    },
    { 
      id: 'quote_sent', 
      name: '📋 Quote Sent', 
      color: 'bg-yellow-100 border-yellow-300',
      description: 'They have your pricing',
      nextStep: 'Follow up in 3-5 days'
    },
    { 
      id: 'negotiating', 
      name: '🤝 Negotiating', 
      color: 'bg-orange-100 border-orange-300',
      description: 'Going back and forth on details',
      nextStep: 'Address their concerns and close the deal'
    },
    { 
      id: 'won', 
      name: '🎉 Won!', 
      color: 'bg-green-100 border-green-300',
      description: 'You got the job!',
      nextStep: 'Create project and schedule work'
    },
    { 
      id: 'lost', 
      name: '❌ Lost', 
      color: 'bg-red-100 border-red-300',
      description: 'They went another direction',
      nextStep: 'Learn what happened and move on'
    }
  ];

  useEffect(() => {
    fetchDeals();
  }, []);

  const fetchDeals = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/crm/pipeline`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch deals');

      const data = await response.json();
      setDeals(data.deals || []);
    } catch (error) {
      console.error('Error fetching deals:', error);
    } finally {
      setLoading(false);
    }
  };

  const addNewDeal = async (dealData) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/crm/pipeline/deals`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dealData)
      });

      if (response.ok) {
        fetchDeals();
        setShowAddDeal(false);
      }
    } catch (error) {
      console.error('Error adding deal:', error);
    }
  };

  const moveDeal = async (dealId, newStage) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/crm/pipeline/deals/${dealId}/stage`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ stage: newStage })
      });

      if (response.ok) {
        fetchDeals();
      }
    } catch (error) {
      console.error('Error moving deal:', error);
    }
  };

  const handleDragStart = (e, deal) => {
    setDraggedDeal(deal);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetStage) => {
    e.preventDefault();
    if (draggedDeal && draggedDeal.stage !== targetStage) {
      moveDeal(draggedDeal.id, targetStage);
    }
    setDraggedDeal(null);
  };

  const getDealsByStage = (stageId) => {
    return deals.filter(deal => deal.stage === stageId);
  };

  const getTotalValue = (stageId) => {
    const stageDeals = getDealsByStage(stageId);
    return stageDeals.reduce((sum, deal) => sum + (deal.value || 0), 0);
  };

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);
  };

  const deleteDeal = async (dealId) => {
    if (!window.confirm('Are you sure you want to delete this opportunity?')) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/crm/pipeline/deals/${dealId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        fetchDeals();
      }
    } catch (error) {
      console.error('Error deleting deal:', error);
    }
  };

  const DealCard = ({ deal }) => (
    <div
      draggable
      onDragStart={(e) => handleDragStart(e, deal)}
      className="bg-white rounded-lg border-2 border-gray-200 p-4 mb-3 cursor-move hover:shadow-lg transition-shadow relative"
    >
      {/* Delete button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          deleteDeal(deal.id);
        }}
        className="absolute top-2 right-2 text-slate-500 hover:text-red-600 transition-colors"
        title="Delete opportunity"
      >
        <XCircle className="h-4 w-4" />
      </button>

      <div className="flex items-start justify-between mb-2 pr-6">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <GripVertical className="h-4 w-4 text-slate-500" />
            <h4 className="font-bold text-lg text-gray-900">{deal.title}</h4>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <Building2 className="h-3 w-3" />
            <span>{deal.company_name}</span>
          </div>
          {deal.contact_name && (
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <User className="h-3 w-3" />
              <span>{deal.contact_name}</span>
            </div>
          )}
        </div>
        <div className="text-right">
          <p className="text-lg font-bold text-green-600">{formatMoney(deal.value)}</p>
        </div>
      </div>

      {deal.notes && (
        <p className="text-sm text-gray-700 mb-3 line-clamp-2">{deal.notes}</p>
      )}

      <div className="flex gap-2">
        <Button size="sm" variant="outline" className="flex-1" onClick={() => window.location.href = `tel:${deal.phone}`}>
          <Phone className="h-3 w-3 mr-1" />
          Call
        </Button>
        <Button size="sm" variant="outline" className="flex-1" onClick={() => window.location.href = `mailto:${deal.email}`}>
          <Mail className="h-3 w-3 mr-1" />
          Email
        </Button>
      </div>

      {deal.last_contact && (
        <p className="text-xs text-gray-500 mt-2">
          Last contact: {new Date(deal.last_contact).toLocaleDateString()}
        </p>
      )}
    </div>
  );

  const AddDealModal = () => {
    const [formData, setFormData] = useState({
      title: '',
      company_name: '',
      contact_name: '',
      phone: '',
      email: '',
      value: '',
      notes: ''
    });

    const handleSubmit = (e) => {
      e.preventDefault();
      addNewDeal({
        ...formData,
        stage: 'new_lead',
        value: parseFloat(formData.value) || 0
      });
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <CardHeader>
            <CardTitle>Add New Opportunity</CardTitle>
            <p className="text-sm text-gray-600">Enter the details of this potential job</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Job Title * <span className="text-gray-500 font-normal">(e.g., "Office Building Renovation")</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="What's the job?"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Company Name * <span className="text-gray-500 font-normal">(who's hiring you)</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="ABC Construction"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Contact Person</label>
                  <input
                    type="text"
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Phone Number</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="john@company.com"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Estimated Value <span className="text-gray-500 font-normal">(your best guess)</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    value={formData.value}
                    onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                    className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="50000"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Any important details about this opportunity..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex-1">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Opportunity
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowAddDeal(false)} className="flex-1">
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading your pipeline...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Sales Pipeline</h1>
            <p className="text-slate-800">Drag and drop deals as they move forward. Simple as that!</p>
          </div>
          <Button onClick={() => setShowAddDeal(true)} size="lg">
            <Plus className="h-5 w-5 mr-2" />
            New Opportunity
          </Button>
        </div>
      </div>

      {/* Help Banner */}
      <Alert className="mb-6 bg-blue-50 border-blue-200">
        <TrendingUp className="h-5 w-5 text-blue-600" />
        <AlertDescription className="text-blue-900">
          <strong>How it works:</strong> Add new opportunities on the left. As you talk to them, drag the card to the right. 
          When you win the job, drag it to "Won!" 🎉
        </AlertDescription>
      </Alert>

      {/* Pipeline Board */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {stages.map((stage) => {
          const stageDeals = getDealsByStage(stage.id);
          const stageValue = getTotalValue(stage.id);

          return (
            <div
              key={stage.id}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, stage.id)}
              className="flex flex-col"
            >
              {/* Stage Header */}
              <div className={`${stage.color} rounded-t-lg border-2 p-4`}>
                <h3 className="font-bold text-gray-900 mb-1">{stage.name}</h3>
                <p className="text-xs text-gray-700 mb-2">{stage.description}</p>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-semibold text-gray-700">{stageDeals.length} deals</span>
                  <span className="font-bold text-green-700">{formatMoney(stageValue)}</span>
                </div>
              </div>

              {/* Cards Container */}
              <div className="flex-1 bg-gray-50 border-2 border-t-0 border-gray-200 rounded-b-lg p-3 min-h-[400px]">
                {stageDeals.length === 0 ? (
                  <div className="text-center text-slate-500 mt-8">
                    <p className="text-sm">No deals here yet</p>
                    {stage.id === 'new_lead' && (
                      <p className="text-xs mt-2">Click "New Opportunity" to add one!</p>
                    )}
                  </div>
                ) : (
                  stageDeals.map((deal) => <DealCard key={deal.id} deal={deal} />)
                )}
              </div>

              {/* Next Step Hint */}
              {stageDeals.length > 0 && (
                <div className="mt-2 p-2 bg-gray-100 rounded text-xs text-gray-600 text-center">
                  <strong>Next:</strong> {stage.nextStep}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Total Pipeline Value */}
      <Card className="mt-6 bg-gradient-to-r from-green-50 to-blue-50 border-green-200">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-semibold">Total Pipeline Value</p>
              <p className="text-4xl font-bold text-green-700">
                {formatMoney(deals.reduce((sum, deal) => sum + (deal.value || 0), 0))}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600 font-semibold">Active Opportunities</p>
              <p className="text-4xl font-bold text-blue-700">{deals.filter(d => !['won', 'lost'].includes(d.stage)).length}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add Deal Modal */}
      {showAddDeal && <AddDealModal />}
    </div>
  );
};

export default SalesPipeline;
